import React from 'react'
import { useTranslation } from 'react-i18next'
import { Home } from 'lucide-react'
import { resolveIcon } from '@/lib/icons'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/**
 * The icons a section or a space can wear.
 *
 * Fifty-five, grouped by what they are about rather than alphabetically, so the
 * grid reads as a small catalogue instead of a wall. They are the same list
 * everywhere: an icon offered when creating and missing when editing would be
 * an icon somebody cannot keep.
 */
export const ICON_OPTIONS = [
  'home', 'folder', 'star', 'heart', 'users', 'user',
  'calendar', 'book-open', 'graduation-cap', 'trophy', 'award', 'medal',
  'image', 'video', 'music', 'file-text', 'shapes', 'newspaper', 'book',
  'megaphone', 'activity', 'trending-up', 'zap', 'flame', 'rocket',
  'briefcase', 'shopping-bag', 'shopping-cart', 'credit-card', 'dollar-sign',
  'message-square', 'message-circle', 'mail', 'bell', 'phone',
  'map-pin', 'globe', 'compass', 'navigation', 'target',
  'settings', 'hammer', 'wrench', 'code', 'cpu',
  'lightbulb', 'sparkles', 'palette', 'puzzle', 'gift',
  'shield', 'lock', 'key', 'crown', 'gem'
] as const

/** `book-open` is stored; the component comes from the icon map. */
export const iconComponent = (stored: string) => resolveIcon(stored, Home)

interface IconPickerProps {
  value: string
  onChange: (icon: string) => void
  disabled?: boolean
  id?: string
  className?: string
}

/**
 * One button showing the icon, one popover holding the rest.
 *
 * It was written four times — twice for sections, once for the space dialog and
 * once inside the wizard — with four copies of the same list, the same search
 * and the same outside-click. Four copies is where the next icon gets added to
 * three of them.
 */
export function IconPicker({ value, onChange, disabled, id, className }: IconPickerProps) {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isOpen) return

    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }

    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [isOpen])

  /* Matched against the stored name and the translated one, so "livro" finds
     `book` in a Portuguese session and `book` still finds itself. */
  const visible = React.useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return ICON_OPTIONS as readonly string[]

    return (ICON_OPTIONS as readonly string[]).filter(
      (name) => name.includes(term) || t(`icons.${name}`).toLowerCase().includes(term)
    )
  }, [search, t])

  const Current = iconComponent(value)

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      <button
        type="button"
        id={id}
        onClick={() => setIsOpen((open) => !open)}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={t('fields.icon')}
        className="flex h-9 w-[60px] items-center justify-center rounded-md border border-input bg-white px-2 transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Current className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-[380px] rounded-md border bg-white shadow-lg">
          <div className="sticky top-0 border-b bg-white p-2">
            <Input
              placeholder={t('search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-8 text-sm"
              autoFocus
              onClick={(event) => event.stopPropagation()}
            />
          </div>

          <div className="max-h-[280px] overflow-y-auto p-2">
            {visible.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">{t('empty')}</div>
            ) : (
              <div className="grid grid-cols-8 gap-1">
                {visible.map((name) => {
                  const Icon = iconComponent(name)

                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => {
                        onChange(name)
                        setIsOpen(false)
                        setSearch('')
                      }}
                      title={t(`icons.${name}`)}
                      className={cn(
                        'flex aspect-square items-center justify-center rounded p-2 transition-colors hover:bg-gray-100',
                        value === name && 'bg-primary-50 ring-2 ring-primary hover:bg-primary-50'
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
